export type Rarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary' | 'mythic';

/** Permanent passive effect rolled when an item is ascended. One per ascension tier. */
export type GlitchPerk = 'vampiric' | 'overloaded' | 'hollow';

export type ItemKind =
  | 'hardware'
  | 'energy_cell'
  | 'data_track'
  | 'chip'
  | 'junk'
  | 'weapon'
  | 'helmet'
  | 'armor'
  | 'accessory'
  | 'shield'
  | 'belt'
  | 'sash'
  | 'shoulder';

export type EquipmentSlot = 'weapon' | 'helmet' | 'armor' | 'accessory' | 'shield' | 'belt' | 'sash' | 'shoulder';

export type ResourceId =
  | 'scrap'
  | 'energy_cell'
  | 'nano_chip'
  | 'data_track'
  | 'hyper_processor_5';

export interface EquipmentStats {
  attack: number;
  defense: number;
  health: number;
}

// ── Rolled item stat variation ─────────────────────────────────
export type BonusStatKey = 'attack' | 'defense' | 'health' | 'loot_chance' | 'xp_gain';

export interface BonusStat {
  stat: BonusStatKey;
  /** Flat integer for combat stats; fraction for multipliers (0.05 = +5%). */
  value: number;
  isMultiplier: boolean;
}

// ── Weapon classes ────────────────────────────────────────────
// In v2 combat-math each class has a normalized DPS (mult × speed = 2.0):
//  heavy    — 0.8 hits/sec × 2.5 mult = burst & armor-pen specialist
//  standard — 2.0 hits/sec × 1.0 mult = balanced all-rounder
//  fast     — 4.0 hits/sec × 0.5 mult = on-hit-effect machine
export type WeaponClass = 'heavy' | 'fast' | 'standard';

/** Per-class default attack profile. Items can override individual fields on ItemDef. */
export interface WeaponProfile {
  /** Hits per second. */
  attackSpeed: number;
  /** Multiplicative bonus to base damage. */
  damageMult: number;
}

export const WEAPON_PROFILES: Record<WeaponClass, WeaponProfile> = {
  heavy:    { attackSpeed: 0.8, damageMult: 2.5 },
  standard: { attackSpeed: 2.0, damageMult: 1.0 },
  fast:     { attackSpeed: 4.0, damageMult: 0.5 },
};

// ── Item sets ──────────────────────────────────────────────────
// Hollow-Knight is the sole equipment set after the 2026-05 HK-only cleanup.
// Kept as a union (not a string) so adding a future set is a localized change.
export type ItemSetId = 'hollow_knight' | 'vault_knight';

// ── Legendary unique effects ───────────────────────────────────
export type LegendaryEffect =
  | 'armor_penetration'   // ignore enemy DEF completely
  | 'death_prevention'    // once-per-dungeon revive + 50% HP heal
  | 'credits_to_atk'      // +1 ATK per 500k credits (max +200)
  | 'gambler_fallacy'     // bad-luck protection: +legend% per miss
  | 'arcane_focus'        // +50% damage on the FIRST hit of a fresh enemy (full HP)
  | 'chain_lightning';    // 30% chance to also strike a nearby enemy for 60% damage

// ── Skills ────────────────────────────────────────────────────
export type SkillId =
  | 'execute'               // Build 1 active
  | 'momentum'              // Build 1 passive
  | 'shield_bash'           // Build 2 active
  | 'kinetic_absorption'    // Build 2 passive
  | 'market_manipulation'   // Build 3 active
  | 'compounding_interest'  // Build 3 passive
  | 'treasure_hunter'       // Build 4 active
  | 'salvage_expert'        // Build 4 passive
  | 'fireball'              // Ranged: 3.5× dmg, ignores 50% DEF
  | 'lightning_bolt'        // Instant: 4× dmg, no counter
  | 'frost_lance'           // Ranged: 3× dmg, freezes enemy visually
  | 'frost_nova'            // P2.B Bulwark: AOE 2× ATK + 5s DEF buff + slow tag
  | 'phantom_burst'         // P3 Phantom: 2.8× ATK guaranteed-crit, hits 4 crowd
  | 'engineer_overload';    // P3 Engineer: 3.2× ATK, ignores 60% DEF, 2 crowd

export type SkillType = 'active' | 'passive';

// ── 3D Mesh attachment (drives the 2.5D Three.js renderer) ────
/**
 * Where on the character a 3D mesh attaches.
 * - `hand_right/left`: weapons mounted on the wrist bone
 * - `helmet`: head bone
 * - `torso`: chest bone (rarely used; armor usually swaps body instead)
 * - `body`: special — the entire character body is swapped to this glTF
 *   (used by armor items so different rarities show as different Quaternius outfits)
 */
export type MeshSlot = 'helmet' | 'torso' | 'hand_right' | 'hand_left' | 'body' | 'belt' | 'sash' | 'shoulder';

export interface MeshAsset {
  /** Public path to the .glb / .gltf / .obj file. */
  path: string;
  /** Where on the character skeleton this mesh attaches (or 'body' for full-character swap). */
  slot: MeshSlot;
  /**
   * How the mesh attaches:
   *   - 'static' (default): mounted on a single bone via SLOT_BONE_CANDIDATES,
   *     follows that bone's transform. Used for helmets, weapons, shields.
   *   - 'skinned': asset ships as a SkinnedMesh sharing the body's Bip01 rig;
   *     loader re-binds the skin to body's bones at equip time, so the piece
   *     animates with multiple bones (chest piece + arms etc.). Used for
   *     modular armour (vault_chest / vault_shoulders / vault_belt).
   */
  kind?: 'static' | 'skinned';
  /** Optional uniform scale override. */
  scale?: number;
  /** Optional position offset relative to the bone (m). */
  offset?: [number, number, number];
  /** Optional rotation offset relative to the bone (radians). */
  rotation?: [number, number, number];
  /** Optional hex colour for tinting (used for sets / ascended items). */
  tintColor?: string;
  /** Optional emissive colour — gives legendary weapons a self-glow on the cel-shaded body. */
  emissive?: string;
  /** Emissive intensity (default 0). 1.5–2.5 for visible glow. */
  emissiveIntensity?: number;
  /** Source ItemDef.id — set by getMeshLayers so the renderer can look up
   *  per-item tuning overrides from the Equipment Studio. */
  itemId?: string;
}

export interface ItemDef {
  id: string;
  kind: ItemKind;
  rarity: Rarity;
  nameKey: string;
  baseValue: number;
  stackable: boolean;
  equipSlot?: EquipmentSlot;
  stats?: EquipmentStats;
  maxUpgradeLevel?: number;
  weaponClass?: WeaponClass;
  /** Override the WEAPON_PROFILES default attackSpeed (hits/sec). */
  attackSpeed?: number;
  /** Override the WEAPON_PROFILES default damageMult. */
  damageMult?: number;
  /** Fraction of enemy DEF ignored on damage calc, 0..1. (Old binary armor_penetration → 1.0). */
  armorPenPct?: number;
  setId?: ItemSetId;
  /** P2.B Aspekt-affinity: when worn, contributes 1 piece toward the
   *  matching aspect's affinity-tier (3/6/8 piece thresholds). Items have
   *  AT MOST one affinity tag, so a fully-built loadout maxes out one
   *  aspect-slot at most. */
  aspectAffinity?: import('./aspects').AspectAffinityTag;
  legendaryEffect?: LegendaryEffect;
  isExclusiveBossDrop?: boolean;
  /** 3D visual descriptor — drives the CharacterScreen portrait + CombatView3D. */
  mesh?: MeshAsset;
  /** Special attack visual override — renders a unique effect on top of the default swing/projectile.
   *  - 'lightning': hand-orb + chain VFX (legendary_lightning)
   *  - 'fire': fire-burst flash on each melee hit (legendary_sword)
   */
  attackVisual?: 'lightning' | 'fire';
}

export interface InventoryStack {
  itemId: string;
  quantity: number;
}

export interface Inventory {
  slots: InventoryStack[];
  maxSlots: number;
}

export interface EquipmentInstance {
  id: string;
  itemId: string;
  upgradeLevel: number;
  /** Stats rolled at drop time. Overrides ItemDef.stats for this instance. */
  rolledStats?: EquipmentStats;
  /** Extra bonus rolls beyond base stats (scale with rarity). */
  bonusStats?: BonusStat[];
  /** Legacy boolean — preserved for v1 saves. New code reads ascensionTier. */
  ascended?: boolean;
  /** 0 = not ascended, 1..3 = ascension tier. Tier 3 = Mythic rarity. */
  ascensionTier?: number;
  /** Glitch-perks rolled at each ascension (length matches ascensionTier). */
  glitchPerks?: GlitchPerk[];
}

export type EquippedMap = Partial<Record<EquipmentSlot, EquipmentInstance>>;

export type ResourceCost = {
  credits?: number;
  premium?: number;
  cleanCode?: number;
  corruptedData?: number;
  neonGems?: number;
} & Partial<Record<ResourceId, number>>;

/** Per-decrypter persistent state. Tracks purchased level + buffered CD + Gem-bought storage upgrade. */
export interface DecrypterState {
  /** 0 = not purchased; 1+ = active level. */
  level: number;
  /** Currently buffered Corrupted Data, capped at storageMax × storageMultiplier. */
  buffer: number;
  /** Multiplier from Neon-Gem upgrades (default 1, doubled to 2, etc). */
  storageMultiplier?: number;
  /** Fractional Clean-Code carry-over (0..1). Prevents truncation losing sub-integer output per tick. */
  cleanCodeRemainder?: number;
}

export interface AutoMinerDef {
  id: string;
  nameKey: string;
  descriptionKey: string;
  tier: 1 | 2 | 3 | 4;
  baseCost: ResourceCost;
  costMultiplier: number;
  yield: Partial<Record<ResourceId | 'credits', number>>;
  tickSeconds: number;
  unlockLevel: number;
}

export type BoostEffect = 'damage' | 'defense' | 'heal' | 'loot_chance';

export interface BoostDef {
  id: string;
  nameKey: string;
  descriptionKey: string;
  effect: BoostEffect;
  value: number;
  durationSec: number;
  cost: ResourceCost;
}

export interface ActiveBoost {
  boostId: string;
  expiresAt: number;
}

export type VaultRequirement =
  | { type: 'level'; value: number }
  | { type: 'equipped_slot'; slot: EquipmentSlot }
  | { type: 'min_stat'; stat: 'attack' | 'defense' | 'health'; value: number }
  | { type: 'equipped_rarity'; minRarity: Rarity }
  | { type: 'min_power'; value: number };

/** Class picker on character-create. Forward-compat — currently HK is the
 *  only playable class. Stored on each character so a future add of e.g.
 *  Phantom doesn't require migrating every existing character. */
export type CharacterClass = 'hollow_knight';

/** Slim metadata for the character-select UI. Loaded as a batch via
 *  `listCharacters(userId)` so the screen renders without pulling each
 *  character's equipment + inventory + boosts up-front. */
export interface CharacterSummary {
  /** Character UUID — matches Supabase `characters.id`, used as Player.id. */
  id: string;
  /** Owning account, null for local-mode characters. */
  userId: string | null;
  /** Slot 0..2 — pinned by the (user_id, character_index) unique index. */
  characterIndex: number;
  characterClass: CharacterClass;
  name: string;
  level: number;
  /** Last-played epoch ms — used to sort "Continue last" + show in card. */
  lastTickAt: number;
}

export interface Player {
  /** Character UUID. Server-side this matches `characters.id`. For local
   *  mode it's `local-${ts}-${rand}`. */
  id: string;
  /** Account-owner UUID. Matches `auth.users.id` / `profiles.id`. NULL
   *  for local-mode characters (those have no account binding). */
  userId?: string | null;
  /** Slot 0..2 within the owner's character list. Always 0 for legacy
   *  single-character saves (migration backfills index=0). */
  characterIndex?: number;
  /** Class picked at character creation (forward-compat — HK only today). */
  characterClass?: CharacterClass;
  name: string;
  level: number;
  xp: number;

  /** Schema version. v1 = legacy credits/research. v2+ = CD-economy with cleanCode. */
  gameVersion?: number;

  /** Legacy primary currency. Reset to 0 on v2 migration; new code reads cleanCode. */
  credits: number;
  /** Legacy premium currency. Renamed to neonGems in v2 — kept for migration. */
  premium: number;

  /** Legacy v2 spend-currency. Sunset in P2.A.5 — value is migrated 0.5×
   *  into corruptedData. Kept optional for migration backward-compat only;
   *  fresh players have it as 0. */
  cleanCode?: number;
  /** v2: raw farming output from Combat — now the single spend-currency
   *  after the Decrypter sunset, fed directly into Augment-Tree. */
  corruptedData: number;
  /** v2: premium currency (cosmetics, future micro-transactions). */
  neonGems: number;

  /** Legacy Overclock-Tap heat gauge. Sunset in P2.A.5, kept optional for
   *  migration only — fresh players never write this. */
  heat?: number;
  overheatedUntil?: number;

  inventory: Inventory;
  equipmentStash: EquipmentInstance[];
  equipment: EquippedMap;

  /** Legacy idle miner counts. Reset on v2 migration; replaced by `decrypters` (legacy too). */
  autoMiners: Record<string, number>;
  /** Legacy v2 Decrypter-network state. Sunset in P2.A.5 — refunded
   *  proportionally to corruptedData / neonGems by migration v12. Kept
   *  optional so old saves still type-check during the transition window. */
  decrypters?: Record<string, DecrypterState>;

  boosts: ActiveBoost[];
  lastTickAt: number;

  currentHp: number;
  createdAt: number;
  updatedAt: number;

  // Prestige & progression
  highestVaultTier: number;

  // Build system
  skillSlots: [SkillId | null, SkillId | null]; // [active, passive]
  unlockedSkills: SkillId[];

  // Market Manipulation duration
  marketManipEndsAt: number; // epoch ms, 0 if inactive

  // Auto-farmer (idle background farming)
  autoFarmerVaultId: string | null; // vault currently being auto-farmed, null = off
  lastAutoFarmAt: number;           // epoch ms of last successful auto-farm tick

  // Avatar
  avatarColor: string; // 'blue' | 'red' | 'green' | 'purple' | 'orange' | 'cyan' | 'gold' | 'dark'

  /** Legacy linear research nodes. Reset on v2 migration; replaced by skillWebNodes. */
  research: Record<string, number>;
  /** v2: skill-web node levels (Core / Synergy / Capstone rings). */
  skillWebNodes: Record<string, number>;

  // World Boss cooldown tracking
  lastBossKillAt: Record<string, number>; // bossId → epoch ms of last kill

  /** Universal ascension currency. Earned by salvaging L10 items (+1 each).
   *  Replaces the old "must own 3 exact-duplicate L10 items" wall — fragments
   *  can be earned across any item-type and spent on any ascension. Stored in
   *  the profile.meta JSONB blob; defaults to 0 for legacy saves. */
  ascensionFragments: number;
  /** Counter that increments on each run that doesn't drop a legendary, +0 on
   *  drops. Adds a small chance bonus on legendary rolls — soft-pity to keep
   *  long farming sessions productive. Capped server-side at 0.30 (30% bonus). */
  legendaryPity: number;
  /** Endless-Mode highscore: highest wave the player has REACHED (entered) in
   *  the Endless Fight arena. Persists for the leaderboard. 0 = never tried.
   *  Optional for legacy-save compatibility — code reads as `?? 0`. */
  bestEndlessWave?: number;
  /** Skill-Web Respec-Count: 0 = never respeced (1st respec free), N → next
   *  respec costs 1000 CD. Interim before P2.B Skill-Web sunset. */
  skillWebRespecCount?: number;

  // ── Retention Foundation (P1) ───────────────────────────────
  /** Cosmetics the player owns. Key = cosmetic id from COSMETICS catalog.
   *  unlockedAt = epoch ms. equipped = whether currently active (mutually
   *  exclusive within a cosmetic CATEGORY, see cosmetics.ts). Empty object
   *  for fresh players — defaults are baked-in (no unlocked record needed
   *  for the free `avatarColor` value). */
  unlockedCosmetics?: Record<string, { unlockedAt: number; equipped: boolean }>;
  /** Achievement progress + unlock-state. Key = achievement id. progress
   *  is the running counter (1 for binary achievements). unlockedAt set
   *  when threshold reached; reward-claim is separate (see claimedAt). */
  achievements?: Record<string, { unlockedAt?: number; progress: number; claimedAt?: number }>;
  /** Currently-active daily quest. Auto-resets at 04:00 UTC via the seed
   *  `Date.toISOString().slice(0,10)`. null means no quest assigned yet
   *  (or just-reset and waiting for next pull). */
  dailyQuest?: { id: string; progress: number; claimedAt: number; resetAt: number } | null;
  /** Tutorial step counter — drives the TutorialOverlay sequence. 0 = fresh
   *  player, 99 = tutorial completed. Each major milestone (first mob,
   *  first drop, first vault clear, etc.) bumps this. */
  tutorialStep?: number;

  /** Rolling backup of the player state taken just before a destructive
   *  Zustand-persist version migration runs. One slot, FIFO — each new
   *  migration overwrites the previous backup. Expires after 30 days so the
   *  localStorage payload stays bounded. Used by `gameStore.rollbackTo*()`
   *  support paths to undo a bad migration. */
  _legacyBackup?: { fromVersion: number; createdAt: number; snapshot: Player };

  /** P2.A Augment-Tree: number of nodes purchased per branch (0..5). Nodes
   *  unlock sequentially within a branch — `survivor: 3` means nodes 1, 2, 3
   *  are owned, node 4 is the next purchasable. Each node grants a stat
   *  multiplier (see `src/domain/augments.ts`). */
  augments?: {
    survivor: number;
    aggressor: number;
    hunter: number;
    engineer: number;
    mystic: number;
  };

  /** P2.B Aspekt-System: build-identity layer over augments. Player picks
   *  1 primary + 1 secondary (latter unlocks at Tau-Vault-Clear). `levels`
   *  is per-aspect 0..5 (0 = not active, 1..5 = invested via CD). `unlocked`
   *  is the set of aspects the player can currently slot. */
  aspects?: {
    primary: import('./aspects').AspectId;
    secondary: import('./aspects').AspectId | null;
    levels: Record<import('./aspects').AspectId, number>;
    unlocked: import('./aspects').AspectId[];
  };
  /** Active-skill pool the player has unlocked across aspects. Independent
   *  of currently-slotted aspect — once an aspect has been picked once,
   *  its active skill is permanently in the pool. */
  unlockedActiveSkills?: import('./aspects').AspectActiveSkillId[];
  /** Currently-equipped active skill from the pool. */
  activeSkillId?: import('./aspects').AspectActiveSkillId | null;

  /** Run-streak tracker: epoch-ms timestamps of recent ACTIVE run-ends
   *  (Run + Farm; Auto-Farmer ticks deliberately don't count so AFK doesn't
   *  trivialise the bonus). Pruned to entries within RUN_STREAK_WINDOW_MS
   *  (30 min). Drives a small loot+XP multiplier on the NEXT run so that
   *  sustained engagement is rewarded over single-click farming. */
  runStreak?: number[];
}

// ---- Dungeon / Combat ----

export interface DropEntry {
  itemId: string;
  chance: number;
  quantity: number;
}

/**
 * Special attack ability for bosses (T4+). Used by `MovingEnemy` to spawn
 * telegraphed AOE / charge / chain attacks on a per-boss cooldown.
 *
 * - `charge`:    boss-side red line player→boss; on release the boss "leaps"
 *                toward the player and hits for `damageMult × baseAttack`.
 * - `aoe_pulse`: growing colored ring under the boss; on release every entity
 *                inside `radius` takes `damageMult × baseAttack` (incl. player).
 * - `chain`:     spawns a ChainLightning visual hitting up to N nearby mobs
 *                AND the player; each link does `damageMult × baseAttack`.
 */
export interface BossAttackDef {
  kind: 'charge' | 'aoe_pulse' | 'chain';
  /** Visual warning duration before the actual hit lands. 0 = no telegraph (instant). */
  telegraphMs: number;
  /** How long between same-kind casts. */
  cooldownMs: number;
  /** Multiplier on `calcEnemyDamage()` output for this attack. */
  damageMult: number;
  /** AOE radius in world units (only for `aoe_pulse`). */
  radius?: number;
  /** Color hint for the telegraph visual (CSS color). Defaults by kind. */
  color?: string;
}

export interface EnemyDef {
  id: string;
  nameKey: string;
  maxHp: number;
  attack: number;
  defense: number;
  xpReward: number;
  /** Legacy v1: direct credit drop on kill. v2 economy uses corruptedDataReward instead. */
  creditReward: number;
  /** v2: Corrupted-Data dropped on kill (raw farming output, must be processed by Decrypter). */
  corruptedDataReward: number;
  drops: DropEntry[];
  tier: number;
  meshPath?: string;
  meshScale?: number;
  /** How the enemy engages: melee charges to point-blank, ranged stops at ~5u and shoots projectiles. */
  attackKind?: 'melee' | 'ranged';
  /** Optional boss-tier special attacks (telegraphed AOE / charge / chain).
   *  Only fires if defined; basic mobs leave this empty and use only the
   *  per-tick contact damage in autoTick. */
  attacks?: BossAttackDef[];
}

/** A single live enemy in the current Archero-style room. */
export interface ActiveEnemy {
  instanceId: string;
  enemyId: string;
  hp: number;
  maxHp: number;
}

export interface WaveDef {
  enemies: string[];
}

export type DropTier = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';

export interface VaultDef {
  id: string;
  nameKey: string;
  descriptionKey: string;
  minLevel: number;
  tier: number; // 1–5
  requirements: VaultRequirement[];
  waves: WaveDef[];
  dropTier: DropTier;
}

export interface DungeonLoot {
  resources: Array<{ itemId: string; quantity: number }>;
  equipment: EquipmentInstance[];
}

export interface CombatState {
  phase: 'idle' | 'selecting' | 'combat' | 'victory' | 'defeat';
  vaultId: string | null;
  waveIndex: number;
  enemyIndex: number;
  playerHp: number;
  playerMaxHp: number;
  enemyHp: number;
  enemyMaxHp: number;
  currentEnemy: EnemyDef | null;
  specialCharge: number;
  loot: Array<{ itemId: string; quantity: number }>;
  equipmentLoot: EquipmentInstance[];
  earnedXp: number;
  /** v2: Corrupted Data accumulated this run (was earnedCredits in v1). */
  earnedCorruptedData: number;
  /** Epoch ms when the run entered combat. 0 while idle. Used by the
   *  aspect-telemetry hook to compute run-duration on victory/flee/defeat. */
  runStartAt?: number;
  /** Which entry-point spawned the current run. Used so the run-streak
   *  push at claimLoot can exclude Farm clicks (streak rewards manual
   *  engagement, not instant-resolve clicks). */
  runMode?: 'manual' | 'farm' | 'endless' | 'boss';
  /** Epoch ms of the last P3 Engineer hp_regen tick. Engineer's
   *  Repair-Protocol passive heals % of maxHp per REAL second; this
   *  bookkeeping lets autoTick compute elapsed-since-last-regen without
   *  relying on tick frequency (which varies by weapon attack-speed). */
  lastHpRegenAt?: number;
}
